import { useMemo } from "react";

/**
 * Oct 17, 2026 (Randy) — THE DIG, drawn as a core sample.
 *
 * The old readout said "digging threads 500–1,000 deep", which counts a number
 * nobody can picture. But the backfill walks commentThreads with order="time",
 * strictly newest → oldest, so DEPTH IS TIME: 500 threads deep is really "back
 * to about March 2023". Every comment already carries publishedAt.
 *
 * So the dig is drawn on a real date axis, read RIGHT TO LEFT:
 *   right edge  = today / the newest comment on the channel (the surface)
 *   left edge   = the channel's own beginning (bedrock)
 *   filled band = how much history has actually been excavated
 *   drill head  = where the cursor has reached, labelled with its date
 *   notches     = drafts found, plotted in the year they were written
 *
 * Horizontal rather than vertical on purpose: a hole is the instinct, but a
 * horizontal seam reads as a timeline and survives a phone screen.
 *
 * ⚠ The axis is only honest BECAUSE the walk is ordered by time. If the
 * backfill ever moves to order="relevance", this bar starts lying.
 */

const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;

function yearOf(iso) {
  if (!iso) return null;
  const y = Number(String(iso).slice(0, 4));
  return Number.isFinite(y) && y > 2000 ? y : null;
}

function monthLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 4);
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

export function DigCore({ map, busy = false, compact = false, channelSlot = "channel1" }) {
  const model = useMemo(() => {
    const startIso = map?.channel_started_at || "";
    const newestIso = map?.newest_at || new Date().toISOString();
    const cursorIso = map?.cursor_at || "";
    const start = startIso ? new Date(startIso).getTime() : NaN;
    const newest = new Date(newestIso).getTime();
    const cursor = cursorIso ? new Date(cursorIso).getTime() : NaN;
    const haveSpan = Number.isFinite(start) && Number.isFinite(newest) && newest > start;
    const span = haveSpan ? newest - start : 0;

    // Position as a percentage from the LEFT edge. Newest sits at 100%.
    const pos = (ms) => {
      if (!haveSpan || !Number.isFinite(ms)) return null;
      return Math.max(0, Math.min(100, ((ms - start) / span) * 100));
    };

    const cursorPct = pos(cursor);
    // Excavated = everything from the drill head rightward to the surface.
    const dugFrom = cursorPct === null ? (map?.at_bedrock && map?.session_scanned ? 0 : null) : cursorPct;

    // Year ticks, thinned so a phone doesn't turn into a picket fence.
    const ticks = [];
    if (haveSpan) {
      const y0 = new Date(start).getUTCFullYear();
      const y1 = new Date(newest).getUTCFullYear();
      const total = y1 - y0 + 1;
      const step = total <= 8 ? 1 : total <= 16 ? 2 : Math.ceil(total / 8);
      for (let y = y1; y >= y0; y -= step) {
        const p = pos(new Date(Date.UTC(y, 0, 1)).getTime());
        if (p !== null) ticks.push({ year: y, pct: p });
      }
    }

    const years = (map?.years || []).map((row) => ({
      ...row,
      pct: haveSpan ? pos(new Date(Date.UTC(row.year, 6, 1)).getTime()) : null,
    })).filter((r) => r.pct !== null && (r.drafts > 0 || r.queued > 0));

    const maxFinds = years.reduce((m, r) => Math.max(m, r.drafts + r.queued), 0) || 1;

    return {
      haveSpan, startIso, newestIso, cursorIso, cursorPct, dugFrom, ticks, years, maxFinds,
      spanYears: haveSpan ? Math.max(1, Math.round(span / YEAR_MS)) : 0,
      atBedrock: !!map?.at_bedrock,
      everDug: (map?.session_scanned || 0) > 0 || !!cursorIso || years.length > 0,
    };
  }, [map]);

  // Nothing has ever been dug and we have no dates — show the seam empty
  // rather than hiding, so the creator knows the instrument exists.
  const depthLabel = model.cursorIso
    ? `reached ${monthLabel(model.cursorIso)}`
    : model.atBedrock && model.everDug
      ? "surface — next dig starts from newest"
      : "not dug yet";

  return (
    <div
      className="w-full select-none"
      data-testid={`dig-core-${channelSlot}`}
      data-bedrock={model.atBedrock ? "true" : "false"}
      data-cursor-at={model.cursorIso || ""}
    >
      {/* Legend rail — deeper is left, surface is right. */}
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <span className="text-[9px] uppercase tracking-[0.16em] text-[#8A8884] inline-flex items-center gap-1">
          <span aria-hidden="true">←</span> deeper
        </span>
        <span
          className="text-[10px] tabular-nums text-[#3A3A38] font-medium truncate"
          data-testid={`dig-core-depth-label-${channelSlot}`}
        >
          {busy ? "digging…" : depthLabel}
        </span>
        <span className="text-[9px] uppercase tracking-[0.16em] text-[#8A8884]">newest</span>
      </div>

      {/* The seam itself. */}
      <div className="relative">
        {/* Finds, plotted above the seam in the year they were written. */}
        {!compact && model.years.length > 0 && (
          <div className="relative h-5 mb-0.5" aria-hidden="true">
            {model.years.map((r) => {
              const h = 3 + Math.round(((r.drafts + r.queued) / model.maxFinds) * 13);
              return (
                <span
                  key={r.year}
                  title={`${r.year} · ${r.drafts} draft${r.drafts === 1 ? "" : "s"}${r.queued ? ` · ${r.queued} queued` : ""}`}
                  className="absolute bottom-0 w-[3px] rounded-t-sm"
                  style={{
                    left: `calc(${r.pct}% - 1.5px)`,
                    height: `${h}px`,
                    background: r.drafts > 0 ? "#2C6E92" : "#C9BFAE",
                    opacity: r.drafts > 0 ? 0.85 : 0.6,
                  }}
                />
              );
            })}
          </div>
        )}

        <div
          className="relative h-7 rounded-md overflow-hidden border border-[#D9D2C6]"
          style={{
            background:
              "repeating-linear-gradient(180deg, #F5F1E9 0px, #F5F1E9 5px, #EFEADF 5px, #EFEADF 10px)",
          }}
        >
          {/* Excavated ground: from the drill head rightward to the surface.
              Deliberately the heavier tone — exposed earth should pull the eye
              harder than ground nobody has touched yet. */}
          {model.dugFrom !== null && (
            <div
              className="absolute inset-y-0 transition-all duration-700 ease-out"
              style={{
                left: `${model.dugFrom}%`,
                right: 0,
                background:
                  "repeating-linear-gradient(180deg, rgba(44,110,146,0.30) 0px, rgba(44,110,146,0.30) 5px, rgba(44,110,146,0.42) 5px, rgba(44,110,146,0.42) 10px)",
                borderLeft: "1px solid rgba(28,92,122,0.65)",
              }}
              data-testid={`dig-core-excavated-${channelSlot}`}
            />
          )}

          {/* Bedrock cap — the channel's first comment. Hatched, on the left. */}
          {model.haveSpan && (
            <div
              className="absolute inset-y-0 left-0 w-1.5"
              style={{
                background:
                  "repeating-linear-gradient(45deg, #B9AE9A 0px, #B9AE9A 2px, #CFC6B6 2px, #CFC6B6 4px)",
              }}
              title={`Channel begins ${monthLabel(model.startIso)}`}
              aria-hidden="true"
            />
          )}

          {/* Year ticks, drawn on the seam at their true dates. */}
          {model.ticks.map((t) => (
            <span
              key={t.year}
              className="absolute top-0 bottom-0 border-l border-[#C9BFAE]/70"
              style={{ left: `${t.pct}%` }}
              aria-hidden="true"
            />
          ))}

          {/* The drill head. */}
          {model.cursorPct !== null && (
            <span
              className={`absolute top-0 bottom-0 w-[2px] bg-[#1C5C7A] transition-all duration-700 ease-out ${busy ? "animate-pulse" : ""}`}
              style={{ left: `calc(${model.cursorPct}% - 1px)` }}
              data-testid={`dig-core-head-${channelSlot}`}
              aria-hidden="true"
            >
              <span className="absolute -top-px left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#1C5C7A]" />
            </span>
          )}
        </div>

        {/* Year labels beneath, aligned to the same positions. */}
        <div className="relative h-4 mt-0.5" aria-hidden="true">
          {model.ticks.map((t) => (
            <span
              key={t.year}
              className="absolute text-[9px] tabular-nums text-[#8A8884] -translate-x-1/2"
              style={{ left: `${t.pct}%` }}
            >
              {t.year}
            </span>
          ))}
        </div>
      </div>

      {/* One honest line of prose under the instrument. */}
      <div className="text-[10px] text-[#6B6A68] leading-snug mt-0.5">
        {model.haveSpan ? (
          <>
            <span className="tabular-nums">{model.spanYears}</span> year
            {model.spanYears === 1 ? "" : "s"} of comment history
            {model.atBedrock && model.everDug
              ? " · fully walked — the next dig starts again from your newest"
              : model.cursorIso
                ? " · keep digging to go further back"
                : " · nothing excavated yet"}
          </>
        ) : (
          "Dig once to map this channel's comment history."
        )}
      </div>
    </div>
  );
}

export default DigCore;
